import type { EvolutionId } from './systems/evolutions';

export const EVT = {
  RoomCleared: 'room_cleared',
  EnemyKilled: 'enemy_killed',
  LootPicked: 'loot_picked',
  CatHit: 'cat_hit',
  CatDied: 'cat_died',
  ExtractionReached: 'extraction_reached',
  BastetDialog: 'BastetDialog',
  EvolutionUnlocked: 'evolution_unlocked',
  RoomEnter: 'room_enter',
  DoorOpened: 'door_opened',
  HazardSpawned: 'hazard_spawned',
  CombatStarted: 'combat_started',
} as const;

/**
 * Phase 2h — Bastet boss dialog event payload.
 *  - phase 1: Bastet has just spawned (intro line)
 *  - phase 2: Bastet has transitioned to phase 2 (mid-fight line)
 *  - 'defeat': Bastet has just been killed (final line)
 */
export type BastetDialogPayload = { phase: 1 | 2 | 'defeat' };

/** Phase 2t — evolution unlock payload. */
export type EvolutionUnlockedPayload = { id: EvolutionId };

/** Phase 3j — special room entry payload. */
export type RoomEnterPayload = { kind: 'treasure' | 'shrine' };

/**
 * Typed event payload map. Add an entry here when introducing a new
 * narrowed event so callers get compile-time payload validation. Events
 * not listed here keep loose typing.
 */
export type GameEventPayloads = {
  [EVT.BastetDialog]: BastetDialogPayload;
  [EVT.EvolutionUnlocked]: EvolutionUnlockedPayload;
  [EVT.RoomEnter]: RoomEnterPayload;
};

type NarrowedEvent = keyof GameEventPayloads;
/** Conditional exclusion: an event string is "loose" only if it is NOT one
 *  of the keys in `GameEventPayloads`. Used as a guard on the loose `emit`
 *  overload so narrowed events can't slip through with the wrong payload. */
type LooseString<E extends string | symbol> = E extends NarrowedEvent ? never : E;

type Handler = (...args: unknown[]) => void;
type HandlerEntry = { fn: Handler; once: boolean; ctx?: unknown };

// Lazy import to avoid a hard module cycle: events.ts is imported very early
// (Phaser scenes, App.tsx) and errorReporter pulls TelemetryEventRequest from
// @cradle7/shared. We resolve it on first use and cache the function.
let cachedReport: ((message: string, extra?: { stack?: string }) => void) | null = null;
async function loadReporter(): Promise<void> {
  if (cachedReport) return;
  try {
    const mod = await import('../errorReporter');
    cachedReport = (message, extra) => {
      void mod.reportManualError(message, extra);
    };
  } catch {
    cachedReport = () => {
      /* errorReporter unavailable — swallow */
    };
  }
}

/**
 * Phase 2j D1 — pure-TS event bus replacing the prior
 * `Phaser.Events.EventEmitter` singleton. Removing the Phaser import here
 * means modules that only need cross-scene/UI signalling (e.g. App.tsx,
 * BastetDialogOverlay, WaveClearBanner) no longer pull the 339 KB Phaser
 * chunk into the initial bundle waterfall.
 *
 * Overloads on `emit/on/off/once` validate payload shape at call sites
 * for entries in `GameEventPayloads` (e.g.
 * `gameEvents.emit(EVT.BastetDialog, { phase: 'wrong' })` is a compile
 * error). Unknown event names fall back to a loose overload that still
 * accepts arbitrary payloads — keeping legacy call sites typing.
 */
export interface GameEventBus {
  emit<K extends NarrowedEvent>(event: K, payload: GameEventPayloads[K]): boolean;
  emit<E extends string | symbol>(event: LooseString<E>, ...args: unknown[]): boolean;
  on<K extends NarrowedEvent>(
    event: K,
    fn: (payload: GameEventPayloads[K]) => void,
    context?: unknown,
  ): this;
  on<E extends string | symbol>(
    event: LooseString<E>,
    fn: (...args: unknown[]) => void,
    context?: unknown,
  ): this;
  once<K extends NarrowedEvent>(
    event: K,
    fn: (payload: GameEventPayloads[K]) => void,
    context?: unknown,
  ): this;
  once<E extends string | symbol>(
    event: LooseString<E>,
    fn: (...args: unknown[]) => void,
    context?: unknown,
  ): this;
  off<K extends NarrowedEvent>(
    event: K,
    fn?: (payload: GameEventPayloads[K]) => void,
    context?: unknown,
    once?: boolean,
  ): this;
  off<E extends string | symbol>(
    event: LooseString<E>,
    fn?: (...args: unknown[]) => void,
    context?: unknown,
    once?: boolean,
  ): this;
  addListener(event: string | symbol, fn: Handler, context?: unknown): this;
  removeListener(event: string | symbol, fn?: Handler, context?: unknown, once?: boolean): this;
  removeAllListeners(event?: string | symbol): this;
  eventNames(): Array<string | symbol>;
  listeners(event: string | symbol): Handler[];
  listenerCount(event: string | symbol): number;
}

class TypedEventBus implements GameEventBus {
  private readonly handlers = new Map<string | symbol, HandlerEntry[]>();

  emit(event: string | symbol, ...args: unknown[]): boolean {
    const entries = this.handlers.get(event);
    if (!entries || entries.length === 0) return false;
    // Snapshot before iterating so listeners that call off/once mutating
    // the live array don't skip siblings or re-enter dead entries.
    const snapshot = entries.slice();
    for (const entry of snapshot) {
      try {
        entry.fn.apply(entry.ctx, args);
      } catch (err) {
        // Изоляция: ошибка в одном handler'е не должна сломать остальные.
        // ВНИМАНИЕ: это поведенческое отличие от Phaser EventEmitter, который
        // пропагирует ошибки наружу. Если кому-то нужна errors-propagate
        // семантика, нужно переписать TypedEventBus или вызывать listener'ы
        // без try/catch.
        // Чтобы ошибки не оставались невидимыми — форвардим их в
        // errorReporter (telemetry), плюс пишем в console.error.
        // eslint-disable-next-line no-console
        console.error('[gameEvents] handler error', event, err);
        const message = `[gameEvents:${String(event)}] ${
          err instanceof Error ? err.message : String(err)
        }`;
        const stack = err instanceof Error ? err.stack : undefined;
        void loadReporter().then(() => cachedReport?.(message, { stack }));
      }
      if (entry.once) {
        const idx = entries.indexOf(entry);
        if (idx >= 0) entries.splice(idx, 1);
      }
    }
    if (entries.length === 0) this.handlers.delete(event);
    return true;
  }

  on(event: string | symbol, fn: Handler, ctx?: unknown): this {
    const list = this.handlers.get(event) ?? [];
    list.push({ fn, once: false, ctx });
    this.handlers.set(event, list);
    return this;
  }

  once(event: string | symbol, fn: Handler, ctx?: unknown): this {
    const list = this.handlers.get(event) ?? [];
    list.push({ fn, once: true, ctx });
    this.handlers.set(event, list);
    return this;
  }

  off(event: string | symbol, fn?: Handler, ctx?: unknown, _once?: boolean): this {
    const list = this.handlers.get(event);
    if (!list) return this;
    if (!fn) {
      this.handlers.delete(event);
      return this;
    }
    const idx = list.findIndex((e) => e.fn === fn && (ctx === undefined || e.ctx === ctx));
    if (idx >= 0) list.splice(idx, 1);
    if (list.length === 0) this.handlers.delete(event);
    return this;
  }

  addListener(event: string | symbol, fn: Handler, ctx?: unknown): this {
    return this.on(event, fn, ctx);
  }

  removeListener(event: string | symbol, fn?: Handler, ctx?: unknown, once?: boolean): this {
    return this.off(event, fn, ctx, once);
  }

  removeAllListeners(event?: string | symbol): this {
    if (event === undefined) this.handlers.clear();
    else this.handlers.delete(event);
    return this;
  }

  eventNames(): Array<string | symbol> {
    return Array.from(this.handlers.keys());
  }

  listeners(event: string | symbol): Handler[] {
    const list = this.handlers.get(event);
    return list ? list.map((e) => e.fn) : [];
  }

  listenerCount(event: string | symbol): number {
    return this.handlers.get(event)?.length ?? 0;
  }
}

/**
 * Singleton event bus for Phaser scenes ↔ React UI communication.
 * Both sides emit and listen on this bus.
 */
export const gameEvents: GameEventBus = new TypedEventBus();
